import { useElementSize } from "@prismicio/editor-support/React"
import {
  Box,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  FormSearchInput,
  Skeleton,
} from "@prismicio/editor-ui"
import { useEffect, useMemo, useRef, useState } from "react"

import type { Filter } from "../DocumentListBody/ActionBar/FilterDropdown"
import {
  getFilterType,
  getFilterTypeStatus,
} from "../DocumentListBody/ActionBar/utils"
import { DocumentSearchContent } from "../DocumentSearchContent"
import {
  SearchDialogContext,
  useDocumentSearch,
} from "../DocumentSearchContent/useDocumentSearch"

export function SearchBar() {
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const [searchBarSize, setSearchBarSize] = useState<ResizeObserverSize>({
    blockSize: 0,
    inlineSize: 0,
  })
  const [searchBarElement, setSearchBarElement] = useState<
    HTMLInputElement | undefined
  >()

  const searchBarRef = useElementSize<HTMLInputElement>(
    ({ blockSize, inlineSize }, element) => {
      if (
        blockSize === searchBarSize.blockSize &&
        inlineSize === searchBarSize.inlineSize
      )
        return
      setSearchBarSize({ blockSize, inlineSize })
      setSearchBarElement(element)
    },
    [searchBarSize],
  )

  const dialogComboBoxInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    function keyDownHandler(event: KeyboardEvent) {
      if (event.metaKey && event.key === "k") {
        setIsSearchOpen((value) => !value)
        if (!isSearchOpen) {
          searchBarElement?.focus()
        }
      }
    }

    window.addEventListener("keydown", keyDownHandler)
    return () => window.removeEventListener("keydown", keyDownHandler)
  }, [isSearchOpen, searchBarElement])

  const isDropdown = searchBarSize.inlineSize > 420

  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const filters = useMemo<SearchDialogContext["filters"]>(() => {
    const trimmedSearchTerm = searchTerm.trim()
    const hasFiltersApplied =
      trimmedSearchTerm !== "" || selectedFilters.length > 0

    return {
      searchTerm: trimmedSearchTerm,
      customType: getFilterType(selectedFilters, "customType"),
      tag: getFilterType(selectedFilters, "tag"),
      authors: getFilterType(selectedFilters, "author"),
      status: getFilterTypeStatus(selectedFilters),
      hasFiltersApplied,
    }
  }, [selectedFilters, searchTerm])

  useEffect(() => {
    // If the search is OPEN and the selectedFilters change, let's refocus the search input
    // This solves the frustration from losing the search input focus after changing the filter
    if (!isSearchOpen || !searchBarElement) return

    if (dialogComboBoxInputRef.current) {
      dialogComboBoxInputRef.current.focus()
      // Browser will select the text, place the cursor at the end of the input
      const inputValue = dialogComboBoxInputRef.current.value
      dialogComboBoxInputRef.current.setSelectionRange(
        inputValue.length,
        inputValue.length,
      )
    } else {
      searchBarElement.focus()
    }
  }, [isSearchOpen, selectedFilters, searchBarElement])

  const { data, isFetching, isPending } = useDocumentSearch({
    filters,
  })

  const documents = data && filters.hasFiltersApplied ? data.results : []
  const loading = isFetching || (isPending && filters.hasFiltersApplied)

  // refs used for nested dropdowns to not close the main dropdown when clicking on them
  const typeFilterDropdownRef = useRef<HTMLDivElement>(null)
  const tagFilterDropdownRef = useRef<HTMLDivElement>(null)
  const statusFilterDropdownRef = useRef<HTMLDivElement>(null)
  const authorFilterDropdownRef = useRef<HTMLDivElement>(null)
  const dropdownRefs = [
    typeFilterDropdownRef,
    tagFilterDropdownRef,
    statusFilterDropdownRef,
    authorFilterDropdownRef,
  ]

  const comboBoxInputProps = {
    maxLength: 100,
    value: searchTerm,
    onValueChange: setSearchTerm,
    placeholder: "Search for page titles",
    endAdornment: true,
  }

  return (
    <>
      <SearchDialogContext.Provider
        value={{
          filters,
          loading,
          documents,
          typeFilterDropdownRef,
          tagFilterDropdownRef,
          statusFilterDropdownRef,
          authorFilterDropdownRef,
        }}
      >
        <Box width="100%" flexDirection="column">
          <ComboBox
            variant="detached-connected"
            isOpen={isSearchOpen && isDropdown}
            onClose={() => setIsSearchOpen(false)}
            dropdownRefs={dropdownRefs}
            overlay
          >
            <ComboBoxInput
              {...comboBoxInputProps}
              onFocus={() => {
                setIsSearchOpen(true)
                // do not open the dropdown on focus internally
                return false
              }}
              ref={searchBarRef}
            />
            <ComboBoxContent maxHeight="full" disableOnClick>
              <DocumentSearchContent
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
              />
            </ComboBoxContent>
          </ComboBox>
        </Box>

        <Dialog
          hasClose={false}
          onOpenChange={(open) => {
            setIsSearchOpen(open)
          }}
          open={isSearchOpen && !isDropdown}
          size="small"
        >
          <DialogTitle hidden>Search for pages</DialogTitle>
          <DialogDescription hidden>
            Enter keywords or apply filters to find the pages you need.
          </DialogDescription>
          <DialogContent gap={0}>
            <ComboBox variant="attached">
              <ComboBoxInput
                {...comboBoxInputProps}
                ref={dialogComboBoxInputRef}
              />
              <ComboBoxContent maxHeight="full" disableOnClick>
                <DocumentSearchContent
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              </ComboBoxContent>
            </ComboBox>
          </DialogContent>
        </Dialog>
      </SearchDialogContext.Provider>
    </>
  )
}

export function SearchBarSkeleton() {
  return <Skeleton width="100%" height={40} />
}

export function DisabledSearchBar() {
  return (
    <Box width="100%" flexDirection="column">
      <FormSearchInput
        value=""
        placeholder="Search for page titles"
        onValueChange={() => {}}
        disabled
      />
    </Box>
  )
}
